import { useCallback, useEffect, useState } from "react";
import {
  AdditionalInterestInput,
  QuoteDocuments,
  useGetBinderDocumentLazyQuery,
  useGetQuoteDocumentsLazyQuery,
} from "@covertree/new-business-graphql";
import { Toast } from "@covertree/utils";

import { PaymentPlanKey } from "../../types";
import { mapPaymentPlanKeyToPaymentSchedule } from "../../utils";

export function useQuoteDocuments(
  policyLocator: string,
  selectedPaymentPlanKey: PaymentPlanKey,
  additionalInterests: AdditionalInterestInput[]
) {
  const [documents, setDocuments] = useState<
    Partial<Record<PaymentPlanKey, QuoteDocuments>>
  >({});

  const [fetchQuoteDocuments, { loading: documentsLoading }] =
    useGetQuoteDocumentsLazyQuery({
      fetchPolicy: "no-cache",
    });

  const [fetchBinderDocument, { loading: binderDocumentLoading }] =
    useGetBinderDocumentLazyQuery({
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    // Reset documents when additional interests changed
    setDocuments({});
  }, [additionalInterests]);

  const getQuoteDocuments = useCallback(async () => {
    if (
      documents[selectedPaymentPlanKey]?.applicationDocumentUrl ||
      documents[selectedPaymentPlanKey]?.quoteProposalUrl
    )
      return;
    try {
      const { data, error } = await fetchQuoteDocuments({
        variables: {
          policyLocator,
          paymentSchedule: mapPaymentPlanKeyToPaymentSchedule(
            selectedPaymentPlanKey
          ),
          additionalInterests,
        },
      });

      if (error) {
        throw error;
      }
      setDocuments((prev) => ({
        ...prev,
        [selectedPaymentPlanKey]: {
          ...prev[selectedPaymentPlanKey],
          ...data?.getQuoteDocuments,
        },
      }));

      return data?.getQuoteDocuments;
    } catch (e) {
      Toast.showError("Failed to prepare Quote Documents");
    }
  }, [
    fetchQuoteDocuments,
    selectedPaymentPlanKey,
    additionalInterests,
    documents,
  ]);

  const getBinderDocument = useCallback(async () => {
    if (documents[selectedPaymentPlanKey]?.binderDocumentUrl)
      return documents[selectedPaymentPlanKey].binderDocumentUrl;

    try {
      const { data, error } = await fetchBinderDocument({
        variables: {
          policyLocator,
          paymentSchedule: mapPaymentPlanKeyToPaymentSchedule(
            selectedPaymentPlanKey
          ),
          additionalInterests,
        },
      });

      if (error) {
        throw error;
      }

      setDocuments((prev) => ({
        ...prev,
        [selectedPaymentPlanKey]: {
          ...prev[selectedPaymentPlanKey],
          binderDocumentUrl: data?.getQuoteDocuments?.binderDocumentUrl,
        },
      }));

      return data?.getQuoteDocuments?.binderDocumentUrl;
    } catch (e) {
      Toast.showError("Failed to get Binder Document");
    }
  }, [
    fetchBinderDocument,
    selectedPaymentPlanKey,
    additionalInterests,
    documents,
  ]);

  return {
    documentsLoading,
    binderDocumentLoading,
    getBinderDocument,
    getQuoteDocuments,
    documents: documents[selectedPaymentPlanKey],
  };
}
